import { useEffect, useState } from 'react';
import { FiMenu, FiChevronDown, FiLogOut, FiBell, FiUser, FiLock } from 'react-icons/fi';
import { AiOutlineSetting } from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, message, Modal, Input } from 'antd';
import { createNotificationSocket } from '../socket';
import { apiFetch, apiUrl } from '../api';
import { cleanUserName } from '../utils/nameHelper';

const Navbar = ({ toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(() => {
    try {
      const stored = localStorage.getItem('session_notifications');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [hasUnread, setHasUnread] = useState(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);

  // Password Modal State
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [modalCurrentPassword, setModalCurrentPassword] = useState('');
  const [modalNewPassword, setModalNewPassword] = useState('');
  const [modalConfirmPassword, setModalConfirmPassword] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const handleLogout = async () => {
    try {
      await apiFetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error during API logout:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  const handleEmployeeOverview = () => {
    navigate('/employees');
  };

  const handleChangePassword = () => {
    setIsChangePasswordOpen(true);
  };

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    if (modalNewPassword !== modalConfirmPassword) {
      setModalError('Passwords do not match');
      return;
    }
    setModalLoading(true);
    setModalError('');
    try {
      const response = await apiFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: modalCurrentPassword, newPassword: modalNewPassword })
      });
      const data = await response.json();
      if (response.ok) {
        message.success(data.message || 'Password changed successfully');
        setIsChangePasswordOpen(false);
        setModalCurrentPassword('');
        setModalNewPassword('');
        setModalConfirmPassword('');
      } else {
        setModalError(data.message || 'Failed to change password');
      }
    } catch {
      setModalError('Connection error. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{"role": "admin", "name": "Peal Ankhiwala"}'));

  useEffect(() => {
    const fetchLatestUser = async () => {
      try {
        const response = await apiFetch('/api/auth/profile');
        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data.user) {
            // Persist the USER object (name/email/role/companyName). Storing the
            // employee object here dropped those identity fields, which cascaded
            // into wrong greeting/email and a role-less sidebar that defaulted to
            // admin.
            localStorage.setItem('user', JSON.stringify(data.data.user));
            setUser(data.data.user);
            window.dispatchEvent(new Event('auth-change'));
          }
        }
      } catch (err) {
        // ignore
      }
    };
    fetchLatestUser();
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      setUser(JSON.parse(localStorage.getItem('user') || '{"role": "admin", "name": "Peal Ankhiwala"}'));
    };
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  const userRole = user.role?.toLowerCase() || 'admin';
  const profileItems = [
    {
      key: 'edit-profile',
      label: 'Edit Profile',
      icon: <FiUser className="w-4 h-4" />
    },
    {
      key: 'change-password',
      label: 'Change Password',
      icon: <FiLock className="w-4 h-4" />
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <FiLogOut className="w-4 h-4" />,
      danger: true
    }
  ];

  const handleProfileMenuClick = ({ key }) => {
    if (key === 'edit-profile') {
      handleEditProfile();
      return;
    }

    if (key === 'employee-overview') {
      handleEmployeeOverview();
      return;
    }

    if (key === 'change-password') {
      handleChangePassword();
      return;
    }

    if (key === 'logout') {
      handleLogout();
    }
  };

  const getNotificationPath = (notification) => {
    if (notification?.meta?.targetPath) {
      return notification.meta.targetPath;
    }

    if (notification?.type === 'visitor-created') {
      return '/visit-requests';
    }

    if (notification?.type === 'visit-approved' || notification?.type === 'visit-approved-bulk') {
      return '/logs';
    }

    return '/dashboard';
  };

  useEffect(() => {
    if (userRole === 'superadmin') return;

    const socket = createNotificationSocket({
      id: user.id,
      name: user.name,
      role: userRole
    });

    if (!socket) return undefined;

    const handleNotification = (notification) => {
      setNotifications((current) => {
        const notifWithDate = {
          ...notification,
          id: notification.id || Math.random().toString(36).substring(2, 9),
          createdAt: notification.createdAt || new Date().toISOString()
        };
        const next = [notifWithDate, ...current].slice(0, 50);
        localStorage.setItem('session_notifications', JSON.stringify(next));
        return next;
      });
      setHasUnread(true);
    };

    socket.on('notification:new', handleNotification);

    // Lightweight heartbeat (parity with econnect) to keep the connection alive
    // and detect dead links. The service replies on 'pong'.
    socket.on('connection-confirmed', (data) => {
      console.log('Notification socket connected:', data?.socketId);
    });
    socket.on('pong', () => {});
    const heartbeat = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping', { timestamp: Date.now() });
      }
    }, 25000);

    return () => {
      clearInterval(heartbeat);
      socket.off('notification:new', handleNotification);
      socket.off('pong');
      socket.off('connection-confirmed');
      socket.disconnect();
    };
  }, [user.id, user.name, userRole]);

  const isVisitRequestsPage = location.pathname.includes('/visit-requests');
  const showNotificationBadge = hasUnread && notifications.length > 0;
  const notificationItems = notifications.length > 0
    ? [
        ...notifications.slice(0, 5).map((notification) => ({
          key: notification.id,
          onClick: () => {
            navigate(getNotificationPath(notification));
          },
          label: (
            <div className="max-w-[320px] whitespace-normal py-1">
              <div className="text-xs font-semibold text-slate-900">{notification.title}</div>
              <div className="mt-1 text-[11px] leading-5 text-slate-500">{notification.message}</div>
            </div>
          )
        })),
        {
          type: 'divider'
        },
        {
          key: 'view-all',
          onClick: () => setIsNotificationsModalOpen(true),
          label: (
            <div className="text-center text-xs font-semibold text-blue-600 hover:text-blue-700 py-1">
              View All Notifications
            </div>
          )
        }
      ]
    : [{
        key: 'empty',
        disabled: true,
        label: <div className="max-w-[260px] py-2 text-sm text-slate-500">No notifications yet.</div>
      }];

  return (
    <header className={`vms-topbar sticky top-0 z-40 transition-all duration-300 ${isVisitRequestsPage ? 'no-border' : ''}`}>
      <div className="flex items-center justify-between h-full">
        <div className="flex items-center">
          <button
            onClick={toggleSidebar}
            className="p-3 mr-4 text-[var(--wm-text)] hover:bg-[var(--wm-page)] dark:hover:bg-white/10 rounded-md lg:hidden"
          >
            <FiMenu className="w-6 h-6" />
          </button>
          <h1 className="vms-topbar-title">
            {user?.companyName || 'Visitor Management System'}
          </h1>
        </div>

        <div className="flex items-center gap-4">
          {userRole !== 'superadmin' && (
            <Dropdown
              menu={{ items: notificationItems }}
              trigger={['click']}
              placement="bottomRight"
              onOpenChange={(open) => {
                if (open) {
                  setHasUnread(false);
                }
              }}
            >
              <button type="button" className="vms-topbar-icon relative">
                <FiBell />
                {showNotificationBadge && <span className="vms-notification-dot" aria-hidden="true" />}
              </button>
            </Dropdown>
          )}
          {user?.role === 'admin' && (
            <div className="relative group/settings">
              <button
                type="button"
                className="vms-topbar-icon"
                title="Settings"
              >
                <AiOutlineSetting className="text-lg transition-transform duration-500 ease-in-out group-hover/settings:rotate-45" />
              </button>

              {/* Settings mega-dropdown */}
              <div className="invisible opacity-0 group-hover/settings:visible group-hover/settings:opacity-100 transition-all duration-200 absolute right-0 top-full mt-2 z-50 bg-white dark:bg-[#1e2535] rounded-xl shadow-2xl border border-slate-100 dark:border-white/10 min-w-[520px] p-6">
                <div className="grid grid-cols-2 gap-6">

                  {/* GENERAL column */}
                  <div>
                    <h6 className="text-[11px] font-bold tracking-widest text-slate-400 uppercase border-b-2 border-blue-500 pb-1 mb-3">General</h6>
                    <ul className="space-y-[6px]">
                      {[
                        // { label: 'Company Settings', path: '/configuration' },
                        { label: 'System Settings', path: '/system-settings' },
                        { label: 'Email Master', path: '/email-master' },
                      ].map(({ label, path }) => (
                        <li key={label}>
                          <button
                            type="button"
                            onClick={() => navigate(path)}
                            className="w-full text-left text-sm text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 py-[3px] transition-colors"
                          >
                            {label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* SYSTEM SETTINGS column */}
                  <div>
                    <h6 className="text-[11px] font-bold tracking-widest text-slate-400 uppercase border-b-2 border-blue-500 pb-1 mb-3">Organization Structure</h6>
                    <ul className="space-y-[6px]">
                      {[
                        // { label: 'Branch', path: '/branches' },
                        { label: 'Department', path: '/departments' },
                        // { label: 'Sub-Department', path: '/sub-departments' },
                        { label: 'Roles', path: '/employee-types' },
                        { label: 'Configuration', path: '/configuration' },
                      ].map(({ label, path }) => (
                        <li key={label}>
                          <button
                            type="button"
                            onClick={() => navigate(path)}
                            className="w-full text-left text-sm text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 py-[3px] transition-colors"
                          >
                            {label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                </div>
              </div>
            </div>
          )}
          <Dropdown menu={{ items: profileItems, onClick: handleProfileMenuClick }} trigger={['click']} placement="bottomRight" classNames={{ root: 'vms-profile-dropdown' }}>
            <button type="button" className="vms-profile-pill">
              <div className="vms-profile-avatar bg-white/90">
                <img
                  src={user.avatar ? (user.avatar.startsWith('data:') || user.avatar.startsWith('http') ? user.avatar : apiUrl(user.avatar)) : `https://ui-avatars.com/api/?name=${cleanUserName(user.name, user.companyName)}&background=03497A&color=ffffff`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="vms-profile-name text-white font-semibold">{cleanUserName(user.name, user.companyName)}</span>
              <FiChevronDown className="w-[18px] h-[18px] text-white" />
            </button>
          </Dropdown>
        </div>
      </div>

      <Modal
        title={null}
        open={isChangePasswordOpen}
        onCancel={() => setIsChangePasswordOpen(false)}
        footer={null}
        closeIcon={null}
        centered
        width={500}
        styles={{
          content: {
            padding: '24px 32px 32px',
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            border: '1px solid #f0f0f0'
          }
        }}
        style={{ top: 30 }}
      >
        <div className="relative">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <h2 className="text-xl font-semibold text-slate-800">Change Password</h2>
            <button
              onClick={() => setIsChangePasswordOpen(false)}
              className="text-slate-400 hover:text-slate-600 transition-colors text-lg"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handlePasswordChangeSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                <span className="text-rose-500 mr-1">*</span> Current Password
              </label>
              <Input.Password
                size="large"
                value={modalCurrentPassword}
                onChange={(e) => setModalCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="h-11 rounded-lg border-slate-200"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                <span className="text-rose-500 mr-1">*</span> New Password
              </label>
              <Input.Password
                size="large"
                value={modalNewPassword}
                onChange={(e) => setModalNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="h-11 rounded-lg border-slate-200"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                <span className="text-rose-500 mr-1">*</span> Confirm Password
              </label>
              <Input.Password
                size="large"
                value={modalConfirmPassword}
                onChange={(e) => setModalConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="h-11 rounded-lg border-slate-200"
                required
              />
            </div>

            {modalError && <div className="text-sm text-red-500 font-medium">{modalError}</div>}

            {/* Footer Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-8">
              <button
                type="button"
                onClick={() => setIsChangePasswordOpen(false)}
                className="px-6 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={modalLoading}
                className="px-6 py-2.5 rounded-lg bg-[#03497a] hover:bg-[#007ac1] text-white font-medium text-sm transition-colors shadow-sm disabled:opacity-50"
              >
                {modalLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Notifications Modal */}
      {userRole !== 'superadmin' && (
        <Modal
          title={null}
          open={isNotificationsModalOpen}
          onCancel={() => setIsNotificationsModalOpen(false)}
          footer={null}
          closeIcon={null}
          centered
          width={600}
          styles={{
            content: {
              padding: '24px 32px 32px',
              borderRadius: '12px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              border: '1px solid #f0f0f0'
            }
          }}
          style={{ top: 30 }}
        >
          <div className="relative">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
              <h2 className="text-xl font-semibold text-slate-800">All Notifications</h2>
              <button
                onClick={() => setIsNotificationsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors text-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => {
                    setIsNotificationsModalOpen(false);
                    navigate(getNotificationPath(notification));
                  }}
                  className="p-3 rounded-lg border border-slate-100 hover:border-blue-100 hover:bg-blue-50/20 cursor-pointer transition-all"
                >
                  <div className="text-sm font-semibold text-slate-900">{notification.title}</div>
                  <div className="mt-1 text-xs text-slate-600">{notification.message}</div>
                  {notification.createdAt && (
                    <div className="mt-2 text-[10px] text-slate-400">
                      {new Date(notification.createdAt).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
              <button
                type="button"
                onClick={() => {
                  setNotifications([]);
                  localStorage.removeItem('session_notifications');
                  setIsNotificationsModalOpen(false);
                }}
                className="px-4 py-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 font-medium text-xs transition-colors"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={() => setIsNotificationsModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium text-xs transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </header>
  );
};

export default Navbar;
